import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, mergeClasses, tokens, Button } from '@fluentui/react-components';
import { Container } from '../../theme/primitives';
import { iconMap } from '../../theme/icons';
import logo1 from '../../assets/img/partners/1.jpg';
import logo2 from '../../assets/img/partners/2.jpg';
import logo3 from '../../assets/img/partners/3.png';
import logo4 from '../../assets/img/partners/4.png';
import logo5 from '../../assets/img/partners/5.jpeg';
import logo6 from '../../assets/img/partners/6.jpg';
import logo7 from '../../assets/img/partners/7.jpg';
import logo8 from '../../assets/img/partners/8.jpg';
import logo9 from '../../assets/img/partners/9.jpeg';
import logo10 from '../../assets/img/partners/10.jpeg';
import logo11 from '../../assets/img/partners/11.jpeg';
import logo12 from '../../assets/img/partners/12.jpeg';
import logo13 from '../../assets/img/partners/13.jpg';
import logo14 from '../../assets/img/partners/14.jpg';

const ChevronLeftIcon = iconMap.chevron_left;
const ChevronRightIcon = iconMap.chevron_right;

const LOGOS = [
  { src: logo1, alt: 'Egger' },
  { src: logo2, alt: 'Cleaf' },
  { src: logo3, alt: 'Thermopal' },
  { src: logo4, alt: 'Kronospan' },
  { src: logo5, alt: 'Gruppo Mauro Saviola' },
  { src: logo6, alt: 'Blum' },
  { src: logo7, alt: 'G*Grass' },
  { src: logo8, alt: 'Formenti & Giovenzana' },
  { src: logo9, alt: 'Schuco' },
  { src: logo10, alt: 'Furnipart' },
  { src: logo11, alt: 'Citterio Giulio' },
  { src: logo12, alt: 'rd rujzdesign' },
  { src: logo13, alt: 'Metakor' },
  { src: logo14, alt: 'Giusti' },
];

const useStyles = makeStyles({
  wrap: {
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: '3rem',
    paddingBottom: '3rem',
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  relative: { position: 'relative' },
  // .no-scrollbar paverstas tiesiogiai — tas pats paternas, kaip NavigationBar.jsx `scroller`.
  scroller: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXXL,
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': { display: 'none' },
    '@media (min-width: 640px)': { gap: '3rem' },
  },
  logo: {
    height: '26px',
    width: 'auto',
    objectFit: 'contain',
    flexShrink: 0,
    scrollSnapAlign: 'start',
    transitionProperty: 'transform',
    transitionDuration: tokens.durationNormal,
    ':hover': { transform: 'scale(1.05)' },
    '@media (min-width: 640px)': { height: '32px' },
  },
  // Realus Fluent <Button appearance="outline" shape="circular"> jau
  // suteikia kraštus/hover pagal Fluent2 standartą (žr. #5) — čia tik
  // plaukiojanti pozicija ir paslėpimas mobiliajame (rodoma tik nuo 640px).
  navButton: {
    display: 'none',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    boxShadow: tokens.shadow4,
    backgroundColor: tokens.colorNeutralBackground1,
    '@media (min-width: 640px)': { display: 'flex' },
  },
  navButtonLeft: { left: '-1rem' },
  navButtonRight: { right: '-1rem' },
  navIcon: { fontSize: '18px' },
  // Slinkties progreso juostelė — TIK mobiliame, kur rodyklių nav mygtukai
  // paslėpti (žr. 40 punktų sąrašo site #15: vartotojas gali nesuprasti,
  // kad juosta apskritai slenkama). 14 logotipų — per daug taškams (dots
  // tinka kelioms skaidrėms), tad progreso juostelė yra tinkamesnis
  // atvaizdavimas nei diskretūs taškai.
  scrollTrack: {
    display: 'block', height: '3px', borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralStroke2, marginTop: tokens.spacingVerticalM,
    marginLeft: 'auto', marginRight: 'auto', width: '64px', overflow: 'hidden',
    '@media (min-width: 640px)': { display: 'none' },
  },
  scrollThumb: {
    height: '100%', borderRadius: tokens.borderRadiusCircular, backgroundColor: tokens.colorBrandBackground,
    transitionProperty: 'transform', transitionDuration: tokens.durationFast,
  },
});

const PartnerLogos = () => {
  const s = useStyles();
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollProgress(max > 0 ? el.scrollLeft / max : 0);
  };

  return (
    <Container size="xl" className={s.wrap}>
      <div className={s.relative}>
        <div ref={scrollRef} className={s.scroller} onScroll={handleScroll}>
          {LOGOS.map(({ src, alt }) => (
            <img
              key={alt}
              src={src}
              alt={alt}
              title={alt}
              className={s.logo}
            />
          ))}
        </div>
        <Button
          appearance="outline"
          shape="circular"
          onClick={() => scrollBy(-1)}
          aria-label={t('shop.partner_logos.scroll_left')}
          icon={<ChevronLeftIcon className={s.navIcon} />}
          className={mergeClasses(s.navButton, s.navButtonLeft)}
        />
        <Button
          appearance="outline"
          shape="circular"
          onClick={() => scrollBy(1)}
          aria-label={t('shop.partner_logos.scroll_right')}
          icon={<ChevronRightIcon className={s.navIcon} />}
          className={mergeClasses(s.navButton, s.navButtonRight)}
        />
      </div>
      <div className={s.scrollTrack} aria-hidden="true">
        <div className={s.scrollThumb} style={{ width: '50%', transform: `translateX(${scrollProgress * 100}%)` }} />
      </div>
    </Container>
  );
};

export default PartnerLogos;
